import React, { useCallback, useMemo, useState } from 'react';
import DataGrid, { Column, Scrolling, SearchPanel, Grouping, GroupPanel, Editing } from 'devextreme-react/data-grid';
import notify from 'devextreme/ui/notify';
import competitionEntityApi from '../../../api/competitionEntityApi';

export function CompetitionRecords({ competitionId, data, recordlists, accessToken, onEntityChange, dataSource }) {
  const [isSaving, setIsSaving] = useState(false);

  const storeItems = dataSource?.store?._array || data || [];

  const recordlistLookup = useMemo(() => (
    recordlists?.map((list) => ({ id: list.recordlistId, name: list.detail || list.shortcode })) || []
  ), [recordlists]);

  const handleSaving = useCallback(async (e) => {
    if (!e.changes?.length) {
      return;
    }

    if (!accessToken) {
      notify('Kein Zugriffstoken verfügbar. Bitte erneut anmelden.', 'warning', 3000);
      e.cancel = true;
      return;
    }

    e.cancel = true;
    setIsSaving(true);

    try {
      for (const change of e.changes) {
        if (change.type === 'insert') {
          const payload = { ...change.data, competitionId };
          const saved = await competitionEntityApi.records.insert(competitionId, payload, accessToken);
          onEntityChange?.('Record', 'insert', saved);
        }

        if (change.type === 'update') {
          const current = storeItems.find((item) => item.recordId === change.key) || {};
          const payload = { ...current, ...change.data, recordId: change.key, competitionId };
          const saved = await competitionEntityApi.records.update(competitionId, change.key, payload, accessToken);
          onEntityChange?.('Record', 'update', saved);
        }

        if (change.type === 'remove') {
          await competitionEntityApi.records.remove(competitionId, change.key, accessToken);
          onEntityChange?.('Record', 'delete', { recordId: change.key });
        }
      }

      e.component.cancelEditData();
    } catch (err) {
      const message = err?.response?.data || err?.message || 'Änderung konnte nicht gespeichert werden';
      notify(message, 'error', 4000);
    } finally {
      setIsSaving(false);
    }
  }, [accessToken, competitionId, data, onEntityChange]);

  return (
    <div className='competition-section' style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className='section-header'>
        <div>
          <div className='section-title'>Rekorde</div>
          <div className='section-subtitle'>Bestehende und neue Rekorde</div>
        </div>
        <div className='section-pill subtle'>{storeItems.length} Rekord{storeItems.length !== 1 ? 'e' : ''}</div>
      </div>

      <DataGrid
        className='grid theme-dependent'
        dataSource={dataSource}
        keyExpr='recordId'
        columnAutoWidth
        rowAlternationEnabled
        hoverStateEnabled
        showBorders={false}
        repaintChangesOnly
        onSaving={handleSaving}
        loadPanel={{ enabled: isSaving, text: 'Speichern...' }}
        style={{ flex: 1 }}
      >
        <SearchPanel visible placeholder='Suche...' />
        <GroupPanel visible />
        <Grouping autoExpandAll={false} />
        <Column
          dataField='recordlistId'
          caption='Rekordliste'
          width={180}
          lookup={{ dataSource: recordlistLookup, valueExpr: 'id', displayExpr: 'name' }}
          groupIndex={0}
        />
        <Column dataField='gender' caption='Geschl.' width={80} />
        <Column dataField='startAge' caption='Von Alter' width={90} />
        <Column dataField='endAge' caption='Bis Alter' width={90} />
        <Column dataField='swimtime' caption='Zeit' width={100} />
        <Column dataField='athleteName' caption='Athlet' width={180} />
        <Column dataField='athleteYob' caption='Jahrgang' width={100} />
        <Column dataField='clubName' caption='Verein' width={150} />
        <Column dataField='cityName' caption='Ort' width={130} />
        <Column dataField='datetime' caption='Datum' dataType='date' format='dd.MM.yyyy' width={110} />
        <Column dataField='pending' caption='Ausstehend' dataType='boolean' width={100} />
        <Editing
          mode='popup'
          allowAdding
          allowUpdating
          allowDeleting
          useIcons
        />
        <Scrolling mode='virtual' />
      </DataGrid>
    </div>
  );
}
